import API from "@/axios";
import { ActionTree } from "vuex";
import { RootState } from "@/store/types";
import {
  BusinessState,
  FetchPaginatedBusinessesPayload,
  ToggleCompanyStatusPayload,
} from "@/modules/businesses/types";
import { FetchPayload } from "@/modules/businesses/types/index";
import { errorParser } from "@/axios/utils/errorParser";

const actions: ActionTree<BusinessState, RootState> = {
  async toggleCompanyStatusPayload(
    { commit, dispatch },
    payload: ToggleCompanyStatusPayload
  ) {
    try {
      const result = await API.post(
        `/companies/${payload.id}/${payload.action}`
      );
      dispatch("fetchCurrentBusiness", payload.id);
      return result;
    } catch (e) {
      const errorMessage = errorParser(e);
      payload.onError(errorMessage);
    }
  },
  fetchBusinesses({ commit, state }, payload: FetchPayload) {
    const { status } = payload;
    const { sortBy, sortOrder } = state;
    const { query, isFetchingBusinesses } = state;
    if (!isFetchingBusinesses) commit("setIsFetchingBusinesses", true);

    const url = `/companies?PageSize=20&status=${status}&search=${query}&sortBy=${sortBy}&sortOrder=${sortOrder}`;
    return API.get(url).then((response) => {
      commit("setIsFetchingBusinesses", false);
      if (response.data) {
        if (status === "all") {
          commit("setBusinesses", response.data.data);
        } else if (status === "approved") {
          commit("setAllApprovedBusinesses", response.data.data);
        } else if (status === "rejected") {
          commit("setAllRejectedBusinesses", response.data.data);
        } else if (status === "pending") {
          commit("setAllPendingBusinesses", response.data.data);
        }
      }
    });
  },
  fetchCurrentBusiness({ commit, dispatch }, businessId) {
    const url = `/companies/${businessId}`;
    return API.get(url).then((response) => {
      if (response.data) {
        const currentBusiness = response.data.data;
        commit("setCurrentBusiness", currentBusiness);
      }
    });
  },
  fetchCurrentBusinessOwners({ commit, state }, businessId) {
    commit("setIsFetchingBusinessOwners", true);
    const { sortOrder, sortBy, isFetchingBusinessOwners } = state;
    if (!isFetchingBusinessOwners) commit("setIsFetchingBusinessOwners", true);
    const url = `/applicants?PageSize=20&sortOrder=${sortOrder}&sortBy=${sortBy}&CompanyId=${businessId}`;
    return API.get(url).then((response) => {
      commit("setIsFetchingBusinessOwners", false);
      if (response.data) {
        commit("setCurrentBusinessOwners", response.data);
      }
    });
  },
  fetchPaginatedBusinesses(
    { commit, state },
    payload: FetchPaginatedBusinessesPayload
  ) {
    const { sortBy, sortOrder } = state;
    const { status } = payload;

    return API.get(
      `/companies?PageSize=20&page=${payload.pageNumber}&status=${status}&sortBy=${sortBy}&sortOrder=${sortOrder}`
    ).then((response) => {
      if (response.data) {
        const { data } = response.data;
        if (data) {
          if (status === "all") {
            commit("setBusinesses", data);
          } else if (status === "approved") {
            commit("setAllApprovedBusinesses", data);
          } else if (status === "rejected") {
            commit("setAllRejectedBusinesses", data);
          } else if (status === "pending") {
            commit("setAllPendingBusinesses", data);
          }
        }
      }
    });
  },
  viewAllBusinesses({ commit, dispatch }) {
    commit("setIsViewing", "all");
    dispatch("fetchBusinesses", { fetchAll: true });
  },
  viewAllApproved({ commit, dispatch }) {
    commit("setIsViewing", "approved");
    dispatch("fetchBusinesses", { status: "approved" });
  },
  viewAllRejected({ commit, dispatch }) {
    commit("setIsViewing", "rejected");
    dispatch("fetchBusinesses", { status: "rejected" });
  },
  viewAllPending({ commit, dispatch }) {
    commit("setIsViewing", "pending");
    dispatch("fetchBusinesses", { status: "pending" });
  },
  setSortOrder({ commit }, sortOrder) {
    commit("setSortOrder", sortOrder);
  },
  setSortBy({ commit }, sortBy) {
    commit("setSortBy", sortBy);
  },
  setQuery({ commit }, query: string) {
    commit("setQuery", query);
  },
};

export default actions;
